import { Navigate, useSearchParams } from 'react-router-dom';

import { CheckoutClaimScreen } from '@/features/onboarding/CheckoutClaimScreen';
import {
  PostCheckoutSetupScreen,
  postCheckoutSetupActive,
} from '@/features/onboarding/postCheckoutSetup';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useBootstrap } from '@/lib/api/hooks';

/**
 * Root-path router.
 *
 * Special case: when the URL has `?welcome=1&session_id=cs_...` (legacy
 * Stripe Checkout Session flow) OR `?welcome=1&payment_intent=pi_...`
 * (new Healthie-flow Payment Element — Stripe's confirmPayment redirects
 * with `payment_intent` as the query param), the user just completed
 * Stripe checkout on the marketing site and was redirected here. Render
 * <CheckoutClaimScreen /> which polls the backend, exchanges the session
 * id for a Supabase magic link, and redirects to it. Once Supabase auth
 * completes, they'll land on /app/orders signed in.
 *
 * Always route through claim — even when a cached Supabase session
 * exists. The patient who just paid may be different from whoever was
 * signed in previously (common during testing); the magic link
 * establishes the correct identity.
 *
 * Otherwise: clinicians land on /clinician-portal, admins on /admin,
 * everyone else on /app. Unauthenticated users fall through to /app and
 * bounce to /login from there.
 *
 * Clinicians are checked FIRST because `is_admin` is true for them too —
 * sending them to /admin would just bounce off RequireAdmin.
 */
export function RootRedirect() {
  const [params] = useSearchParams();
  const { session, loading: authLoading } = useAuth();
  const { data, isLoading: bootLoading } = useBootstrap();

  const isClaimingCheckout =
    (params.has('session_id') || params.has('payment_intent')) &&
    (params.get('welcome') === '1' || params.has('welcome'));

  if (isClaimingCheckout) {
    return <CheckoutClaimScreen />;
  }

  if (authLoading || (session && bootLoading)) {
    if (postCheckoutSetupActive()) return <PostCheckoutSetupScreen />;
    return (
      <div className="min-h-screen flex items-center justify-center type-label-md text-ink-muted">
        Loading…
      </div>
    );
  }

  if (session && data?.is_admin) {
    if (
      data.admin_role === 'clinical' ||
      data.admin_role === 'clinical_supervisor'
    ) {
      return <Navigate to="/clinician-portal" replace />;
    }
    return <Navigate to="/admin" replace />;
  }

  return <Navigate to="/app" replace />;
}
